<template>
  <div class="install-wizard">
    <Wordmark />

    <div v-if="state" class="install-wizard__card">
      <!-- Step 1 — needs-admin -->
      <template v-if="state === 'needs-admin'">
        <div class="install-wizard__step-indicator">
          <div class="install-wizard__step-lbl">
            <span>Create admin account</span>
            <span class="install-wizard__step-of">Step 1 of 3</span>
          </div>
          <div class="install-wizard__progress-track">
            <div class="install-wizard__progress-seg install-wizard__progress-seg--filled" />
            <div class="install-wizard__progress-seg" />
            <div class="install-wizard__progress-seg" />
          </div>
        </div>
        <div class="install-wizard__head">
          <h2>Create your admin account</h2>
          <p class="install-wizard__sub">This becomes the first Administrator on this Goshawk instance, with full access to every module.</p>
        </div>

        <div v-if="error" class="install-wizard__error-banner">
          <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="9" cy="9" r="7" /><path d="M9 5.5v4M9 12.2v.1" /></svg>
          <p>{{ error }}</p>
        </div>

        <form @submit.prevent="submitAdmin">
          <div class="install-wizard__field-row">
            <div class="install-wizard__field">
              <label>First name</label>
              <input v-model="admin.firstName" type="text" placeholder="Toby" required>
            </div>
            <div class="install-wizard__field">
              <label>Last name</label>
              <input v-model="admin.lastName" type="text" placeholder="New" required>
            </div>
          </div>
          <div class="install-wizard__field">
            <label>Email</label>
            <input v-model="admin.email" type="email" placeholder="you@company.com" required>
          </div>
          <div class="install-wizard__field">
            <label>Password</label>
            <input v-model="admin.password" type="password" placeholder="••••••••" required>
          </div>
          <div class="install-wizard__field">
            <label>Confirm password</label>
            <input
              v-model="admin.confirmPassword"
              type="password"
              placeholder="••••••••"
              :class="{ 'install-wizard__input--invalid': passwordMismatch }"
              required
            >
            <div v-if="passwordMismatch" class="install-wizard__mismatch">Passwords don't match yet.</div>
          </div>
          <button type="submit" class="install-wizard__btn-primary" :disabled="submitting || passwordMismatch">
            {{ submitting ? 'Creating account…' : 'Create account →' }}
          </button>
        </form>
      </template>

      <!-- Step 2 — needs-tenant -->
      <template v-else-if="state === 'needs-tenant'">
        <div class="install-wizard__step-indicator">
          <div class="install-wizard__step-lbl">
            <span>Name your organisation</span>
            <span class="install-wizard__step-of">Step 2 of 3</span>
          </div>
          <div class="install-wizard__progress-track">
            <div class="install-wizard__progress-seg install-wizard__progress-seg--filled" />
            <div class="install-wizard__progress-seg install-wizard__progress-seg--filled" />
            <div class="install-wizard__progress-seg" />
          </div>
        </div>
        <div class="install-wizard__head">
          <h2>Name your organisation</h2>
          <p class="install-wizard__sub">This names the tenant/workspace this instance runs as — it appears in the sidebar and account switcher once you're in. You can change it later from Tenants settings.</p>
        </div>

        <div v-if="error" class="install-wizard__error-banner">
          <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="9" cy="9" r="7" /><path d="M9 5.5v4M9 12.2v.1" /></svg>
          <p>{{ error }}</p>
        </div>

        <form @submit.prevent="submitTenant">
          <div class="install-wizard__field">
            <label>Organisation name</label>
            <input v-model="tenant.name" type="text" placeholder="Apexl Studios" required>
            <div class="install-wizard__hint">Shown to every user who signs into this instance.</div>
          </div>
          <button type="submit" class="install-wizard__btn-primary" :disabled="submitting">
            {{ submitting ? 'Saving…' : 'Continue →' }}
          </button>
        </form>
      </template>

      <!-- Step 3 — needs-activation -->
      <template v-else-if="state === 'needs-activation'">
        <div class="install-wizard__step-indicator">
          <div class="install-wizard__step-lbl">
            <span>Licence key</span>
            <span class="install-wizard__step-of">Step 3 of 3</span>
          </div>
          <div class="install-wizard__progress-track">
            <div class="install-wizard__progress-seg install-wizard__progress-seg--filled" />
            <div class="install-wizard__progress-seg install-wizard__progress-seg--filled" />
            <div class="install-wizard__progress-seg install-wizard__progress-seg--filled" />
          </div>
        </div>
        <div class="install-wizard__head">
          <h2>Enter your licence key</h2>
          <p class="install-wizard__sub">The key came with your Goshawk licence email from Hermes.</p>
        </div>

        <div v-if="error" class="install-wizard__error-banner">
          <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="9" cy="9" r="7" /><path d="M9 5.5v4M9 12.2v.1" /></svg>
          <p>{{ error }}</p>
        </div>

        <div class="install-wizard__helper-block">
          <p>At this step Goshawk only checks the key's format (<code>hermes_live_…</code>) and stores it — it doesn't contact Hermes or activate anything yet. Full activation runs the first time you open the Licensing module.</p>
        </div>

        <form @submit.prevent="submitActivation">
          <div class="install-wizard__field">
            <label>Licence key</label>
            <input v-model="activation.token" type="text" placeholder="hermes_live_xxxxxxxxxxxxxxxx" required>
          </div>
          <button type="submit" class="install-wizard__btn-primary" :disabled="submitting">
            {{ submitting ? 'Activating…' : 'Activate →' }}
          </button>
        </form>
      </template>

      <!-- Complete -->
      <template v-else-if="state === 'complete'">
        <div class="install-wizard__success-panel">
          <div class="install-wizard__success-icon">
            <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M4 9.5l3 3 7-7" /></svg>
          </div>
          <h2>Goshawk is installed</h2>
          <p>Your admin account, organisation, and licence key are all set. This installer is now retired for this instance.</p>
          <button type="button" class="install-wizard__btn-primary" @click="goToLogin">Go to sign in →</button>
        </div>
      </template>
    </div>

    <div v-else-if="error" class="install-wizard__card">
      <div class="install-wizard__error-banner install-wizard__error-banner--standalone">
        <svg viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="9" cy="9" r="7" /><path d="M9 5.5v4M9 12.2v.1" /></svg>
        <p>{{ error }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import useApi from '@apexl/icarus-core/src/services/api';
import commonProps from '@apexl/icarus-core/src/components/_properties/common';
import { fetchInstallerStatus, markInstalled } from '@root/services/installerStatus';
import Wordmark from '@root/components/elements/Wordmark.vue';

/* global LOGIN_PAGE_URL */

// `install-wizard.html` (APPROVED mockup) — mounted at `/install` via
// `app\module\installer\Controller\InstallerDisplayController::index()`.
// Owns its own data-fetching/state (status polling on mount, one POST per
// step), mirroring `ServersFleetBoard.vue`'s "whole owned page" shape.
const props = defineProps({
  ...commonProps,
  apiBase: {
    type: String,
    default: '/api/v1/installer',
  },
});

const api = useApi();
const router = useRouter();

// null while the initial status fetch is in flight — nothing renders but
// the wordmark until we know which of the three states to show.
const state = ref(null);
const error = ref(null);
const submitting = ref(false);

const admin = reactive({
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  confirmPassword: '',
});
const tenant = reactive({ name: '' });
const activation = reactive({ token: '' });

// Client-side only — confirm-password never reaches the API. Only flagged
// once the user has actually typed something into the confirm field, so it
// doesn't flash red while the first field is still being filled in.
const passwordMismatch = computed(() => (
  admin.confirmPassword.length > 0 && admin.password !== admin.confirmPassword
));

async function goToLogin() {
  await router.push(`/${LOGIN_PAGE_URL}`);
}

async function loadStatus() {
  error.value = null;
  try {
    const result = await fetchInstallerStatus();
    if (result.installed) {
      // Belt-and-suspenders: the router guard (`installerGuard.js`) should
      // already have redirected away before this component ever mounted on
      // a completed instance. Handled here too in case something reached
      // `/install` directly without going through the guard (e.g. a stale
      // cached page).
      markInstalled();
      await goToLogin();
      return;
    }
    state.value = result.state;
  } catch {
    error.value = 'Could not reach the installer — check your connection and try again.';
  }
}

async function submitAdmin() {
  if (passwordMismatch.value) {
    return;
  }
  error.value = null;
  submitting.value = true;
  try {
    const data = await api.post(`${props.apiBase}/admin`, {
      first_name: admin.firstName,
      last_name: admin.lastName,
      email: admin.email,
      password: admin.password,
    });
    state.value = data.state;
  } catch (err) {
    error.value = err.response?.data?.error || 'Could not create the admin account.';
  } finally {
    submitting.value = false;
  }
}

async function submitTenant() {
  error.value = null;
  submitting.value = true;
  try {
    const data = await api.post(`${props.apiBase}/tenant`, { name: tenant.name });
    state.value = data.state;
  } catch (err) {
    error.value = err.response?.data?.error || 'Could not save the organisation name.';
  } finally {
    submitting.value = false;
  }
}

async function submitActivation() {
  error.value = null;
  submitting.value = true;
  try {
    const data = await api.post(`${props.apiBase}/activation`, { token: activation.token });
    state.value = data.state;
  } catch (err) {
    error.value = err.response?.data?.error || 'Could not save the licence key.';
  } finally {
    submitting.value = false;
  }
}

onMounted(loadStatus);
</script>

<style lang="scss">
.install-wizard {
  position: fixed;
  inset: 0;
  z-index: 1000;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: var(--theme-bg-sidebar);
  padding: 48px 24px 40px;
  font-family: var(--theme-font-body);

  &__card {
    background: var(--theme-bg-card);
    border-radius: var(--theme-radius-lg, 10px);
    box-shadow: var(--theme-shadow-card);
    width: 400px;
    max-width: 100%;
    padding: 30px 32px 28px;
    flex-shrink: 0;
  }

  &__step-indicator {
    margin-bottom: 22px;
  }

  &__step-lbl {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: .06em;
    text-transform: uppercase;
    color: var(--theme-accent-dark);
    margin-bottom: 8px;
  }

  &__step-of {
    color: var(--theme-text-tertiary);
    font-weight: 600;
  }

  &__progress-track {
    display: flex;
    gap: 4px;
  }

  &__progress-seg {
    flex: 1;
    height: 4px;
    border-radius: 2px;
    background: var(--theme-border-soft);

    &--filled {
      background: var(--theme-accent-primary);
    }
  }

  &__head {
    h2 {
      font-size: 18px;
      font-weight: 600;
      margin: 0 0 6px;
      letter-spacing: -.005em;
      color: var(--theme-text-primary);
    }
  }

  &__sub {
    font-size: 12.5px;
    color: var(--theme-text-secondary);
    line-height: 1.55;
    margin: 0 0 22px;
  }

  &__error-banner {
    display: flex;
    gap: 8px;
    align-items: flex-start;
    background: rgba(209, 67, 67, .08);
    border: 1px solid rgba(209, 67, 67, .3);
    border-radius: var(--theme-radius, 6px);
    padding: 10px 12px;
    margin-bottom: 20px;

    &--standalone {
      margin-bottom: 0;
    }

    svg {
      width: 14px;
      height: 14px;
      color: var(--theme-status-danger);
      flex-shrink: 0;
      margin-top: 1px;
    }

    p {
      margin: 0;
      font-size: 12px;
      color: var(--theme-status-danger);
      line-height: 1.5;
    }
  }

  &__field {
    margin-bottom: 16px;
  }

  &__field-row {
    display: flex;
    gap: 12px;

    .install-wizard__field {
      flex: 1;
    }
  }

  &__field label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: var(--theme-text-secondary);
    margin-bottom: 6px;
  }

  &__field input,
  &__field input[type="text"],
  &__field input[type="email"],
  &__field input[type="password"] {
    width: 100%;
    padding: 9px 12px;
    border: 1px solid var(--theme-border-strong);
    border-radius: var(--theme-radius, 6px);
    font-size: 13.5px;
    font-family: var(--theme-font-body);
    color: var(--theme-text-primary);
    background: #fff;

    &::placeholder {
      color: var(--theme-text-tertiary);
    }
  }

  &__input--invalid {
    border-color: var(--theme-status-danger) !important;
  }

  &__hint {
    font-size: 11px;
    color: var(--theme-text-tertiary);
    margin-top: 5px;
    line-height: 1.5;
  }

  &__mismatch {
    font-size: 11px;
    color: var(--theme-status-danger);
    margin-top: 5px;
    line-height: 1.5;
  }

  &__btn-primary {
    width: 100%;
    background: var(--theme-accent-primary);
    color: #fff;
    border: none;
    border-radius: var(--theme-radius, 6px);
    padding: 11px 16px;
    font-size: 13.5px;
    font-weight: 600;
    cursor: pointer;
    margin-top: 6px;
    font-family: var(--theme-font-body);

    &:disabled {
      opacity: .6;
      cursor: not-allowed;
    }
  }

  &__helper-block {
    background: var(--theme-bg-content);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius, 6px);
    padding: 12px 14px;
    margin-bottom: 20px;

    p {
      margin: 0;
      font-size: 12px;
      color: var(--theme-text-secondary);
      line-height: 1.55;
    }

    code {
      font-family: var(--theme-font-mono);
      background: var(--theme-border-soft);
      padding: 1px 5px;
      border-radius: 3px;
      font-size: 11px;
      color: var(--theme-text-primary);
    }
  }

  &__success-panel {
    text-align: center;
    padding: 8px 0 4px;

    h2 {
      font-size: 18px;
      font-weight: 600;
      margin: 0 0 8px;
      color: var(--theme-text-primary);
    }

    p {
      font-size: 12.5px;
      color: var(--theme-text-secondary);
      line-height: 1.6;
      margin: 0 0 24px;
      max-width: 30ch;
      margin-left: auto;
      margin-right: auto;
    }
  }

  &__success-icon {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: rgba(47, 163, 107, .12);
    color: var(--theme-status-success);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 18px;

    svg {
      width: 24px;
      height: 24px;
    }
  }
}
</style>
